import { graphql, useLazyLoadQuery } from "react-relay";
import { generatePath, useNavigate, useSearchParams } from "react-router";

import { POLICY_COLLECTIONS_BREADCRUMB } from "app/constants/breadcrumbs";
import { paths } from "app/constants/paths";
import { useAlertContext } from "app/contexts";
import { AdminPage } from "app/views/admin/AdminPage";

import { UpsertDpcForm } from "../components/UpsertDpcForm";
import { type EditDpcRootQuery } from "./__generated__/EditDpcRootQuery.graphql";

export function EditDpcRoot() {
  const { alertDispatch } = useAlertContext();
  const [searchParams] = useSearchParams({});
  const collectionUUID = searchParams.get("collectionUUID") || "";
  const navigate = useNavigate();

  const data = useLazyLoadQuery<EditDpcRootQuery>(
    graphql`
      query EditDpcRootQuery(
        $uuid: String!
        $accountGroupFilterElement: FilterElementInput
      ) {
        ...AdminPage_query
        policyCollection(uuid: $uuid) {
          name
          repositoryConfig {
            uuid
          }
          ...UpsertDpcForm_policyCollection
        }
        ...UpsertDpcForm_query
      }
    `,
    {
      uuid: collectionUUID || "",
    },
  );

  const onUpdated = (message: string) => {
    alertDispatch({
      message,
      severity: "success",
    });
  };

  function navigateBack() {
    navigate(
      generatePath(paths.adminPolicyCollectionDetails, {
        collectionId: collectionUUID,
      }),
    );
  }

  if (!data.policyCollection) {
    return null;
  }

  return (
    <AdminPage
      parentCrumbs={[
        POLICY_COLLECTIONS_BREADCRUMB,
        // The collection's details page is the trail's parent level.
        {
          label: data.policyCollection.name,
          target: generatePath(paths.adminPolicyCollectionDetails, {
            collectionId: collectionUUID,
          }),
        },
      ]}
      queryRef={data}
      title="Edit Dynamic Policy Collection"
      isForm
    >
      <UpsertDpcForm
        onAdded={(message) => {
          onUpdated(message);
          navigateBack();
        }}
        onCancel={navigateBack}
        queryRef={data.policyCollection}
        queryRefAcl={data}
        repositoryUUID={data.policyCollection?.repositoryConfig?.uuid || ""}
      />
    </AdminPage>
  );
}
